import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import api from '../services/api';
import { format } from 'date-fns';
import { arSA } from 'date-fns/locale';

interface Recipient {
  phoneNumber: string;
  status: 'accepted' | 'rejected' | 'pending';
  rejectionReason?: string;
}

interface SmsMessage {
  _id: string;
  messageId?: string;
  status: 'pending' | 'sent' | 'delivered' | 'failed' | 'scheduled';
  messageType: 'promotional' | 'transactional' | 'notification' | 'reminder' | 'custom';
  body: string;
  sender: string;
  recipients?: Recipient[];
  cost?: {
    amount: number;
    currency: string;
  };
  messageInfo?: {
    messageCount: number;
    totalRecipients: number;
    acceptedCount: number;
    rejectedCount: number;
  };
  scheduling?: {
    isScheduled: boolean;
    scheduledDateTime?: Date;
    scheduleId?: string;
  };
  sentAt?: Date;
  deliveredAt?: Date;
  createdAt: Date;
  error?: {
    code: string;
    message: string;
  };
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const SmsHistoryPage: React.FC = () => {
  const [messages, setMessages] = useState<SmsMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const [filters, setFilters] = useState({
    status: '',
    messageType: '',
    startDate: '',
    endDate: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const [selectedMessage, setSelectedMessage] = useState<SmsMessage | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
   const hasInitialLoad = useRef(false);
   const isFirstSearchEffectRun = useRef(true);
   const prevDebouncedSearch = useRef(debouncedSearch);
   const debouncedSearchRef = useRef(debouncedSearch);

  // تحديث الـ ref عند تغيير debouncedSearch
  useEffect(() => {
    debouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  const loadMessages = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', pagination.limit.toString());

      if (filters.status) params.append('status', filters.status);
      if (filters.messageType) params.append('messageType', filters.messageType);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (debouncedSearchRef.current) params.append('search', debouncedSearchRef.current);

      const response = await api.get(`/marketing/sms/api/messages?${params.toString()}`);

      if (response.data.success) {
        setMessages(response.data.data.messages);
        setPagination(prev => ({
          ...prev,
          page: response.data.page,
          total: response.data.total,
          pages: response.data.pages
        }));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء تحميل الرسائل');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // تحميل البيانات عند فتح الصفحة لأول مرة (mount)
  useEffect(() => {
    // منع التحميل المزدوج في Strict Mode
    if (!hasInitialLoad.current) {
      hasInitialLoad.current = true;
      // استخدام setTimeout لتأخير التحميل قليلاً وتجنب double render
      const timer = setTimeout(() => {
        loadMessages(1);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [loadMessages]);

  // تأثير لتحميل الرسائل عند تغيير الفلاتر (باستثناء البحث)
  useEffect(() => {
    // منع التحميل المزدوج في Strict Mode
    if (hasInitialLoad.current) {
      loadMessages(1);
    }
  }, [filters.status, filters.messageType, filters.startDate, filters.endDate, loadMessages]);

  // تأثير منفصل للبحث مع debouncing
  useEffect(() => {
    // تخطي أول run في Strict Mode (يحدث مرتين)
    if (isFirstSearchEffectRun.current) {
      isFirstSearchEffectRun.current = false;
      return;
    }

    // تحميل البيانات فقط إذا كان debouncedSearch قد تغير عن القيمة السابقة
    if (debouncedSearch !== prevDebouncedSearch.current) {
      prevDebouncedSearch.current = debouncedSearch;
      const handler = setTimeout(() => {
        loadMessages(1);
      }, 500);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [debouncedSearch, loadMessages]);

  // تنظيف الـ timer عند إغلاق المكون
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handlePageChange = (event: unknown, newPage: number) => {
    loadMessages(newPage + 1);
  };

  const handleFilterChange = useCallback((key: string, value: string) => {
    if (key === 'search') {
      // تحديث حالة البحث الفوري
      setFilters(prev => ({
        ...prev,
        [key]: value
      }));
      // إعداد debouncing للبحث
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        setDebouncedSearch(value);
      }, 500);
    } else {
      setFilters(prev => ({
        ...prev,
        [key]: value
      }));
    }
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      status: '',
      messageType: '',
      startDate: '',
      endDate: '',
      search: ''
    });
    setDebouncedSearch('');
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
  }, []);

  const viewDetails = useCallback((message: SmsMessage) => {
    setSelectedMessage(message);
    setDetailsOpen(true);
  }, []);

  const deleteMessage = useCallback(async (id: string) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الرسالة المجدولة؟')) return;

    try {
      await api.delete(`/marketing/sms/api/schedule/${id}`);
      loadMessages(pagination.page);
    } catch (err: any) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء حذف الرسالة');
    }
  }, [pagination.page, loadMessages]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return 'success';
      case 'pending':
      case 'scheduled':
        return 'warning';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'sent':
        return 'مرسلة';
      case 'delivered':
        return 'تم التسليم';
      case 'pending':
        return 'قيد الانتظار';
      case 'scheduled':
        return 'مجدولة';
      case 'failed':
        return 'فاشلة';
      case 'cancelled':
        return 'ملغاة';
      default:
        return status;
    }
  };

  const getMessageTypeLabel = (type: string) => {
    switch (type) {
      case 'promotional':
        return 'ترويجية';
      case 'transactional':
        return 'معاملات';
      case 'notification':
        return 'إشعار';
      case 'reminder':
        return 'تذكير';
      case 'custom':
        return 'مخصصة';
      default:
        return type;
    }
  };

  const formatDate = (date?: Date) => {
    if (!date) return '-';
    return format(new Date(date), 'yyyy/MM/dd HH:mm', { locale: arSA });
  };

  const truncateText = (text: string, maxLength = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom align="right">
        سجل رسائل SMS
      </Typography>

      {/* Filters Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">عوامل التصفية</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              variant={showFilters ? 'contained' : 'outlined'}
            >
              {showFilters ? 'إخفاء الفلاتر' : 'عرض الفلاتر'}
            </Button>
            {(filters.status || filters.messageType || filters.startDate || filters.endDate || filters.search) && (
              <Button
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                color="secondary"
              >
                مسح
              </Button>
            )}
          </Box>
        </Box>

        {showFilters && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="بحث"
                placeholder="ابحث في النص..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>الحالة</InputLabel>
                <Select
                  value={filters.status}
                  label="الحالة"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">الكل</MenuItem>
                  <MenuItem value="pending">قيد الانتظار</MenuItem>
                  <MenuItem value="scheduled">مجدولة</MenuItem>
                  <MenuItem value="sent">مرسلة</MenuItem>
                  <MenuItem value="delivered">تم التسليم</MenuItem>
                  <MenuItem value="failed">فاشلة</MenuItem>
                  <MenuItem value="cancelled">ملغاة</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>نوع الرسالة</InputLabel>
                <Select
                  value={filters.messageType}
                  label="نوع الرسالة"
                  onChange={(e) => handleFilterChange('messageType', e.target.value)}
                >
                  <MenuItem value="">الكل</MenuItem>
                  <MenuItem value="promotional">ترويجية</MenuItem>
                  <MenuItem value="transactional">معاملات</MenuItem>
                  <MenuItem value="notification">إشعار</MenuItem>
                  <MenuItem value="reminder">تذكير</MenuItem>
                  <MenuItem value="custom">مخصصة</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="من تاريخ"
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="إلى تاريخ"
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Messages Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="right">الحالة</TableCell>
                <TableCell align="right">النوع</TableCell>
                <TableCell align="center">النص</TableCell>
                <TableCell align="right">المرسل</TableCell>
                <TableCell align="right">المستلمون</TableCell>
                <TableCell align="right">التكلفة</TableCell>
                <TableCell align="right">تاريخ الإرسال</TableCell>
                <TableCell align="right">الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : messages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      لا توجد رسائل
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                messages.map((message) => (
                  <TableRow key={message._id} hover>
                    <TableCell align="right">
                      <Chip
                        icon={
                          message.status === 'scheduled' ? <ScheduleIcon /> :
                          message.status === 'sent' || message.status === 'delivered' ? <SuccessIcon /> :
                          message.status === 'failed' ? <ErrorIcon /> :
                          <PendingIcon />
                        }
                        label={getStatusLabel(message.status)}
                        color={getStatusColor(message.status) as any}
                        size="small"
                        clickable
                        onClick={() => viewDetails(message)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={getMessageTypeLabel(message.messageType)}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={message.body}>
                        <Typography variant="body2" sx={{ maxWidth: 300 }}>
                          {truncateText(message.body, 50)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">{message.sender}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${message.messageInfo?.acceptedCount || 0}/${message.messageInfo?.totalRecipients || 0}`}
                        size="small"
                        color={(message.messageInfo?.rejectedCount || 0) > 0 ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {message.cost ? `${message.cost.amount.toFixed(3)} ${message.cost.currency}` : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {formatDate(message.scheduling?.scheduledDateTime || message.sentAt || message.createdAt)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="عرض التفاصيل">
                        <IconButton
                          size="small"
                          onClick={() => viewDetails(message)}
                          color="primary"
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {message.status === 'scheduled' && (
                        <Tooltip title="حذف الرسالة المجدولة">
                          <IconButton
                            size="small"
                            onClick={() => deleteMessage(message.scheduling?.scheduleId || message._id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={pagination.total}
          rowsPerPage={pagination.limit}
          page={pagination.page - 1}
          onPageChange={handlePageChange}
          labelRowsPerPage="عدد العناصر في الصفحة:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
        />
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        dir="rtl"
      >
        <DialogTitle>
          تفاصيل الرسالة
          {selectedMessage && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedMessage.messageId ? `معرف خارجي: ${selectedMessage.messageId}` : ''}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedMessage && (
            <Box>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    الحالة
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      icon={
                        selectedMessage.status === 'scheduled' ? <ScheduleIcon /> :
                        selectedMessage.status === 'sent' || selectedMessage.status === 'delivered' ? <SuccessIcon /> :
                        selectedMessage.status === 'failed' ? <ErrorIcon /> :
                        <PendingIcon />
                      }
                      label={getStatusLabel(selectedMessage.status)}
                      color={getStatusColor(selectedMessage.status) as any}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    نوع الرسالة
                  </Typography>
                  <Typography variant="body1">
                    {getMessageTypeLabel(selectedMessage.messageType)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    اسم المرسل
                  </Typography>
                  <Typography variant="body1">{selectedMessage.sender}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    التكلفة
                  </Typography>
                  <Typography variant="body1">
                    {selectedMessage.cost ? `${selectedMessage.cost.amount.toFixed(3)} ${selectedMessage.cost.currency}` : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    تاريخ الإنشاء
                  </Typography>
                  <Typography variant="body1">{formatDate(selectedMessage.createdAt)}</Typography>
                </Grid>
                {selectedMessage.scheduling?.scheduledDateTime && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      تاريخ الجدولة
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedMessage.scheduling.scheduledDateTime)}
                    </Typography>
                  </Grid>
                )}
                {selectedMessage.sentAt && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      تاريخ الإرسال
                    </Typography>
                    <Typography variant="body1">{formatDate(selectedMessage.sentAt)}</Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Message Body */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                نص الرسالة
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedMessage.body}
                </Typography>
              </Paper>

              <Divider sx={{ my: 3 }} />

              {/* Recipients */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                المستلمون ({selectedMessage.recipients?.length || 0})
              </Typography>
              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="right">رقم الهاتف</TableCell>
                      <TableCell align="right">الحالة</TableCell>
                      <TableCell align="right">سبب الرفض</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selectedMessage.recipients || []).map((recipient, index) => (
                      <TableRow key={index}>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                          {recipient.phoneNumber}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={
                              recipient.status === 'accepted' ? 'مقبول' :
                              recipient.status === 'rejected' ? 'مرفوض' :
                              'قيد الانتظار'
                            }
                            color={
                              recipient.status === 'accepted' ? 'success' :
                              recipient.status === 'rejected' ? 'error' :
                              'warning'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {recipient.rejectionReason || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>

              {/* Error Info */}
              {selectedMessage.error && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    معلومات الخطأ
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'error.50' }}>
                    <Typography variant="body2" color="error">
                      <strong>الكود:</strong> {selectedMessage.error.code}
                    </Typography>
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      <strong>الرسالة:</strong> {selectedMessage.error.message}
                    </Typography>
                  </Paper>
                </>
              )}

              {/* Statistics */}
              <Divider sx={{ my: 3 }} />
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {selectedMessage.messageInfo?.totalRecipients || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        إجمالي المستلمين
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">
                        {selectedMessage.messageInfo?.acceptedCount || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        مقبول
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="error">
                        {selectedMessage.messageInfo?.rejectedCount || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        مرفوض
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {selectedMessage.messageInfo?.messageCount || 1}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        عدد الرسائل
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SmsHistoryPage;
